// ExecStudents.js — "นักเรียน: จำนวนและแนวโน้ม" (pos 90/99)
//
// คำถามของหน้านี้: นักเรียนเพิ่มหรือลด ไหลออกที่ระดับไหน และเด็กมาจากไหน
// ตัวเลขสำคัญที่สุดคืออัตราคงอยู่ (retention) ซึ่งไม่มีหน้าไหนในระบบแสดงมาก่อน
import React, { useMemo } from "react";
import { Badge, Col, ProgressBar, Row, Table } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import {
  ExecPage, Panel, SectionTitle, StatCard, EmptyState, ScrollTable,
  YearPicker, useExecYear, useExecData, compareTo,
  fmtNum, fmtPct, toNum,
} from "./ExecShell";

const PROVINCE_COLORS = ["#0d6efd", "#20c997", "#6f42c1", "#fd7e14", "#adb5bd"];

export default function ExecStudents() {
  const { years, yearId, setYearId } = useExecYear();
  const query = useMemo(() => ({ year_id: yearId }), [yearId]);
  const { data, loading, error } = useExecData("/api/executive/stats/students", query);

  const headcount = data?.headcount || {};
  const academic = headcount.academic || {};
  const islamic = headcount.islamic || {};
  const ap = headcount.ap || {};
  const combined = headcount.combined || {};
  const overlap = headcount.overlap || { available: false };
  const previous = headcount.previous || null;

  const multiYear = useMemo(() => (data?.multiYear || { years: [], classes: [], matrix: {} }), [data]);
  const roomLoad = Array.isArray(data?.roomLoad) ? data.roomLoad : [];
  const largestRooms = Array.isArray(data?.largestRooms) ? data.largestRooms : [];
  const retention = data?.retention || { available: false, rows: [] };
  const provinces = useMemo(() => (Array.isArray(data?.provinces) ? data.provinces : []), [data]);
  const dormRegistration = data?.dormRegistration || { available: false };
  const islamicByClass = data?.islamicByClass || { available: false, rows: [] };
  const attrition = useMemo(() => (data?.attrition || { available: false, byMonth: [] }), [data]);

  // แท่งซ้อนจำนวนนักเรียนย้อนหลัง — แต่ละชั้นเป็นหนึ่ง series
  const multiYearChart = useMemo(() => {
    const yearLabels = multiYear.years.map((y) => String(y.year_year));
    const series = multiYear.classes.map((cls) => ({
      name: cls.c_class,
      data: multiYear.years.map((y) => toNum(multiYear.matrix[`${y.year_id}:${cls.c_id}`])),
    }));
    return { yearLabels, series };
  }, [multiYear]);

  const multiYearOptions = useMemo(() => ({
    chart: { type: "bar", stacked: true, toolbar: { show: false } },
    plotOptions: { bar: { borderRadius: 3, columnWidth: "56%" } },
    dataLabels: { enabled: false },
    legend: { position: "top", fontSize: "12px" },
    xaxis: { categories: multiYearChart.yearLabels, labels: { style: { fontSize: "11px" } } },
    yaxis: { labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    tooltip: { shared: true, intersect: false },
  }), [multiYearChart.yearLabels]);

  const provinceOptions = useMemo(() => ({
    chart: { type: "donut" },
    labels: provinces.map((p) => p.province),
    colors: provinces.map((_, index) => PROVINCE_COLORS[index % PROVINCE_COLORS.length]),
    legend: { position: "bottom", fontSize: "12px" },
    dataLabels: { enabled: false },
    plotOptions: { pie: { donut: { size: "70%" } } },
    stroke: { width: 2 },
  }), [provinces]);

  const attritionOptions = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#dc3545"],
    plotOptions: { bar: { borderRadius: 3, columnWidth: "48%" } },
    dataLabels: { enabled: false },
    xaxis: { categories: attrition.byMonth.map((m) => m.month), labels: { style: { fontSize: "11px" } } },
    yaxis: { labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
  }), [attrition]);

  // ยอดรวมคือ "จำนวนคน" ที่ไม่ซ้ำ ไม่ใช่ผลบวกของทุกภาค — นักเรียนเกือบทุกคนมีทะเบียน
  // ทั้งสามัญและศาสนา การบวกกันจะนับคนกลุ่มนี้สองรอบ
  const totalAll = toNum(combined.total);
  const prevTotalAll = previous?.combined ? toNum(previous.combined.total) : null;

  return (
    <ExecPage
      title="นักเรียน: จำนวนและแนวโน้ม"
      icon="ri-group-line"
      lead="จำนวนนักเรียนเทียบปีก่อน อัตราคงอยู่รายชั้น ขนาดห้อง และภูมิลำเนา"
      activeYear={data?.year}
      loading={loading}
      error={error}
      toolbar={<YearPicker years={years} yearId={yearId} onChange={setYearId} activeYear={data?.year} />}
    >
      <Row className="g-3 mb-4">
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-team-fill" color="primary" label="นักเรียนทั้งหมด"
            value={fmtNum(totalAll)}
            sub={overlap.available
              ? `นับเป็นจำนวนคน ไม่ใช่ผลบวกของภาค · เรียนศาสนาอย่างเดียว ${fmtNum(overlap.islamicOnly)} คน`
              : `ชาย ${fmtNum(combined.male)} · หญิง ${fmtNum(combined.female)}`}
            compare={compareTo(totalAll, prevTotalAll, { suffix: " คน" })}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-book-open-fill" color="info" label="ภาคสามัญ"
            value={fmtNum(academic.total)}
            sub={`ชาย ${fmtNum(academic.male)} · หญิง ${fmtNum(academic.female)}`}
            compare={compareTo(academic.total, previous?.academic?.total, { suffix: " คน" })}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-moon-clear-fill" color="success" label="ภาคศาสนา"
            value={islamic.available === false ? "—" : fmtNum(islamic.total)}
            sub={islamic.available === false ? "ยังไม่มีข้อมูล" : `ชาย ${fmtNum(islamic.male)} · หญิง ${fmtNum(islamic.female)}`}
            compare={islamic.available === false ? null : compareTo(islamic.total, previous?.islamic?.total, { suffix: " คน" })}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-bear-smile-fill" color="warning" label="อนุบาล-ประถม"
            value={ap.available === false ? "—" : fmtNum(ap.total)}
            sub={ap.available === false ? "ยังไม่เปิดใช้งานโมดูล" : `ชาย ${fmtNum(ap.male)} · หญิง ${fmtNum(ap.female)}`}
            compare={ap.available === false ? null : compareTo(ap.total, previous?.ap?.total, { suffix: " คน" })}
          />
        </Col>
      </Row>

      {overlap.available && (
        <div className="alert alert-light border fs-sm py-2 px-3 mb-4 d-flex flex-wrap gap-3 align-items-center">
          <span className="text-secondary">ที่มาของยอดรวม</span>
          <span><i className="ri-checkbox-multiple-line text-primary me-1" />เรียนทั้งสองภาค <strong>{fmtNum(overlap.both)}</strong></span>
          <span><i className="ri-moon-clear-line text-success me-1" />ศาสนาอย่างเดียว <strong>{fmtNum(overlap.islamicOnly)}</strong></span>
          <span><i className="ri-book-open-line text-info me-1" />สามัญอย่างเดียว <strong>{fmtNum(overlap.academicOnly)}</strong></span>
          <span className="fs-xs text-secondary">ผู้ที่เรียนศาสนาอย่างเดียวส่วนใหญ่คือผู้ที่จบ ม.6 แล้ว</span>
        </div>
      )}

      <Row className="g-3 mb-4">
        <Col xl={7}>
          <Panel title="จำนวนนักเรียนย้อนหลัง 5 ปี" note="แยกระดับชั้น ภาคสามัญ">
            {multiYearChart.series.length > 0 ? (
              <ReactApexChart options={multiYearOptions} series={multiYearChart.series} type="bar" height={300} />
            ) : <EmptyState message="ยังไม่มีข้อมูลย้อนหลัง" />}
          </Panel>
        </Col>
        <Col xl={5}>
          <Panel title="ภูมิลำเนา" note="จังหวัดตามที่อยู่ปัจจุบันในทะเบียน">
            {provinces.length > 0 ? (
              <ReactApexChart options={provinceOptions} series={provinces.map((p) => toNum(p.total))} type="donut" height={300} />
            ) : <EmptyState message="ยังไม่มีข้อมูลภูมิลำเนา" />}
          </Panel>
        </Col>
      </Row>

      <SectionTitle
        icon="ri-user-follow-line"
        title="อัตราคงอยู่"
        note={retention.available ? `เทียบนักเรียนปีการศึกษา ${retention.previousYear?.year_year} ว่ายังอยู่ในปีนี้กี่คน` : ""}
        color="success"
      />
      <Row className="g-3 mb-4">
        <Col xl={7}>
          <Panel note="ชั้นสูงสุดของโรงเรียนออกเพราะจบการศึกษา ไม่นับเป็นการหลุดจากระบบ">
            {!retention.available || retention.rows.length === 0 ? (
              <EmptyState message="ยังไม่มีปีการศึกษาก่อนหน้าให้เทียบ" />
            ) : (
              <ScrollTable>
                <Table className="table-sm mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>ชั้นปีก่อน</th>
                      <th className="text-end">นักเรียน</th>
                      <th className="text-end">ยังอยู่</th>
                      <th className="text-end">ออก</th>
                      <th style={{ minWidth: 140 }}>อัตราคงอยู่</th>
                    </tr>
                  </thead>
                  <tbody>
                    {retention.rows.map((row) => (
                      <tr key={row.c_id}>
                        <td>
                          {row.c_class}
                          {row.graduating && <Badge bg="light" text="dark" className="ms-2 fw-normal fs-xs">จบการศึกษา</Badge>}
                        </td>
                        <td className="text-end">{fmtNum(row.previous_total)}</td>
                        <td className="text-end">{fmtNum(row.stayed)}</td>
                        <td className="text-end text-danger">{fmtNum(row.left)}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <ProgressBar
                              className="flex-fill" style={{ height: 7 }}
                              now={toNum(row.rate)}
                              variant={row.graduating ? "secondary" : toNum(row.rate) >= 90 ? "success" : toNum(row.rate) >= 80 ? "warning" : "danger"}
                            />
                            <span className="fs-xs text-secondary" style={{ minWidth: 44 }}>{fmtPct(row.rate)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollTable>
            )}
          </Panel>
        </Col>
        <Col xl={5}>
          <Panel title="นักเรียนที่เปลี่ยนสถานะระหว่างปี" note="จากประวัติการแก้ไขข้อมูล แยกตามเดือน">
            {!attrition.available || attrition.byMonth.length === 0 ? (
              <EmptyState message="ยังไม่มีการเปลี่ยนสถานะในปีนี้" />
            ) : (
              <ReactApexChart
                options={attritionOptions}
                series={[{ name: "เปลี่ยนสถานะ", data: attrition.byMonth.map((m) => toNum(m.total)) }]}
                type="bar" height={280}
              />
            )}
          </Panel>
        </Col>
      </Row>

      <SectionTitle icon="ri-door-open-line" title="ขนาดห้องเรียน" note="ห้องไหนแน่นเกิน" color="warning" />
      <Row className="g-3 mb-4">
        <Col xl={7}>
          <Panel>
            {roomLoad.length === 0 ? <EmptyState /> : (
              <ScrollTable>
                <Table className="table-sm mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>ระดับชั้น</th>
                      <th className="text-end">ห้อง</th>
                      <th className="text-end">นักเรียน</th>
                      <th className="text-end">เฉลี่ย/ห้อง</th>
                      <th className="text-end">มากสุด</th>
                      <th className="text-end">น้อยสุด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roomLoad.map((row) => (
                      <tr key={row.c_id}>
                        <td>{row.c_class}</td>
                        <td className="text-end">{fmtNum(row.room_count)}</td>
                        <td className="text-end">{fmtNum(row.students)}</td>
                        <td className="text-end fw-semibold">{fmtNum(row.avg_per_room)}</td>
                        <td className="text-end">{fmtNum(row.max_per_room)}</td>
                        <td className="text-end">{fmtNum(row.min_per_room)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollTable>
            )}
          </Panel>
        </Col>
        <Col xl={5}>
          <Panel title="ห้องที่นักเรียนมากที่สุด">
            {largestRooms.length === 0 ? <EmptyState compact /> : largestRooms.map((room) => (
              <div key={room.r_id} className="d-flex align-items-center justify-content-between border-bottom py-2 fs-sm">
                <span className="text-truncate">
                  {room.room_label}
                  {room.advisor_name && <span className="text-secondary fs-xs ms-2">{room.advisor_name}</span>}
                </span>
                <span className="fw-semibold flex-shrink-0">{fmtNum(room.students)} คน</span>
              </div>
            ))}
          </Panel>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xl={5}>
          <Panel title="ทะเบียนหอพัก" note="โรงเรียนประจำ — นักเรียนทุกคนต้องมีที่พัก ตัวเลขนี้บอกความครบของทะเบียน ไม่ใช่การแบ่งกลุ่มไปกลับ">
            {!dormRegistration.available ? <EmptyState message="ยังไม่มีข้อมูลหอพัก" compact /> : (
              <>
                <div className="d-flex justify-content-between align-items-end mb-2">
                  <span className="fs-sm text-secondary">ลงทะเบียนแล้ว</span>
                  <span className="fw-semibold fs-4">{fmtPct(dormRegistration.coverageRate)}</span>
                </div>
                <ProgressBar
                  className="mb-3" style={{ height: 10 }}
                  now={toNum(dormRegistration.coverageRate)}
                  variant={toNum(dormRegistration.coverageRate) >= 90 ? "success" : "warning"}
                />
                <div className="d-flex justify-content-between border-bottom py-2 fs-sm">
                  <span><i className="ri-building-4-fill text-primary me-1" />มีทะเบียนหอพัก</span>
                  <span className="fw-semibold">{fmtNum(dormRegistration.allocated)} คน</span>
                </div>
                <div className="d-flex justify-content-between py-2 fs-sm">
                  <span><i className="ri-question-mark text-warning me-1" />ยังไม่ได้ลงทะเบียน</span>
                  <span className="fw-semibold">{fmtNum(dormRegistration.missing)} คน</span>
                </div>
                {toNum(dormRegistration.missing) > 0 && (
                  <div className="fs-xs text-secondary mt-2">
                    <i className="ri-information-line me-1" />
                    นักเรียนกลุ่มนี้ยังไม่มีแถวหอพักในระบบ ไม่ได้แปลว่าไปกลับ
                  </div>
                )}
              </>
            )}
          </Panel>
        </Col>
        <Col xl={7}>
          <Panel title="ภาคศาสนา แยกชั้น" note="รวมห้องพิเศษ AIP และ STP">
            {!islamicByClass.available || islamicByClass.rows.length === 0 ? (
              <EmptyState message="ยังไม่มีข้อมูลภาคศาสนา" compact />
            ) : (
              <ScrollTable>
                <Table className="table-sm mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>ชั้น</th>
                      <th className="text-end">ชาย</th>
                      <th className="text-end">หญิง</th>
                      <th className="text-end">รวม</th>
                    </tr>
                  </thead>
                  <tbody>
                    {islamicByClass.rows.map((row) => (
                      <tr key={row.ic_id}>
                        <td>{row.ic_class}</td>
                        <td className="text-end">{fmtNum(row.male)}</td>
                        <td className="text-end">{fmtNum(row.female)}</td>
                        <td className="text-end fw-semibold">{fmtNum(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollTable>
            )}
          </Panel>
        </Col>
      </Row>
    </ExecPage>
  );
}
